import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const LINKS = [
    { label: "Platform", target: "#platform", testId: "nav-link-platform" },
    { label: "Approach", target: "#approach", testId: "nav-link-approach" },
    { label: "Contact", target: "#contact", testId: "nav-link-contact" },
];

const EASE = [0.16, 1, 0.3, 1];

export const Nav = ({ onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.documentElement.style.overflow = open ? "hidden" : "";
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.documentElement.style.overflow = "";
            document.body.style.overflow = "";
        };
    }, [open]);

    const go = (target) => {
        setOpen(false);
        onNavigate(target);
    };

    return (
        <>
            <header
                data-testid="site-nav"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
                    scrolled
                        ? "border-b border-slate-200/80 bg-white/85 shadow-[0_8px_30px_rgba(10,27,63,0.06)] backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
                    <button
                        data-testid="nav-logo"
                        onClick={() => go("#top")}
                        className="flex items-center"
                        aria-label="EML Synergy — back to top"
                    >
                        <span className="bg-white px-3 py-2 shadow-[0_2px_12px_rgba(10,27,63,0.12)]">
                            <img src="/logo.png" alt="EML Synergy" className="h-11 w-auto" />
                        </span>
                    </button>
                    <nav className="hidden items-center gap-10 md:flex">
                        {LINKS.map((link) => (
                            <button
                                key={link.target}
                                data-testid={link.testId}
                                onClick={() => go(link.target)}
                                className={`text-sm font-medium transition-colors duration-300 ${
                                    scrolled
                                        ? "text-slate-600 hover:text-[#1E3A8A]"
                                        : "text-white/70 hover:text-white"
                                }`}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <button
                            data-testid="nav-request-demo-btn"
                            onClick={() => go("#contact")}
                            className="group hidden items-center gap-2 bg-[#1E3A8A] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#0A1B3F] sm:inline-flex"
                        >
                            Request a demo
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </button>
                        <button
                            data-testid="mobile-menu-btn"
                            onClick={() => setOpen(true)}
                            aria-label="Open menu"
                            className={`transition-colors duration-300 md:hidden ${
                                scrolled ? "text-[#0A1B3F]" : "text-white"
                            }`}
                        >
                            <Menu className="h-6 w-6" />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            data-testid="mobile-menu-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => setOpen(false)}
                            className="fixed inset-0 z-[60] bg-[#0A1B3F]/50 backdrop-blur-sm md:hidden"
                        />
                        <motion.aside
                            data-testid="mobile-menu"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ duration: 0.5, ease: EASE }}
                            className="fixed inset-y-0 right-0 z-[70] flex w-[85%] max-w-sm flex-col bg-[#0A1B3F] text-white md:hidden"
                        >
                            <div className="flex h-[72px] items-center justify-between border-b border-white/10 px-6">
                                <span className="bg-white px-3 py-2">
                                    <img src="/logo.png" alt="EML Synergy" className="h-11 w-auto" />
                                </span>
                                <button
                                    data-testid="mobile-menu-close-btn"
                                    onClick={() => setOpen(false)}
                                    aria-label="Close menu"
                                    className="text-white/70 transition-colors duration-300 hover:text-white"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>
                            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
                                {LINKS.map((link, i) => (
                                    <motion.button
                                        key={link.target}
                                        data-testid={`mobile-${link.testId}`}
                                        initial={{ opacity: 0, x: 32 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: EASE }}
                                        onClick={() => go(link.target)}
                                        className="group flex items-center justify-between border-b border-white/10 py-5 text-left"
                                    >
                                        <span className="font-display text-3xl font-semibold tracking-tight">
                                            {link.label}
                                        </span>
                                        <ArrowUpRight className="h-5 w-5 text-[#7FD8C4] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                    </motion.button>
                                ))}
                            </nav>
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.4, ease: EASE }}
                                className="px-8 pb-10"
                            >
                                <button
                                    data-testid="mobile-request-demo-btn"
                                    onClick={() => go("#contact")}
                                    className="group inline-flex w-full items-center justify-center gap-2 bg-white px-7 py-4 text-sm font-semibold text-[#0A1B3F] transition-colors duration-300 hover:bg-[#7FD8C4]"
                                >
                                    Request a demo
                                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                </button>
                                <a
                                    data-testid="mobile-email-link"
                                    href="mailto:training@emlsynergy.com"
                                    className="mt-6 block text-center text-sm text-slate-400 transition-colors duration-300 hover:text-white"
                                >
                                    training@emlsynergy.com
                                </a>
                            </motion.div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};
